/**
 * Aba Equipe: quem acessa o painel da empresa.
 *
 * Decisoes nao obvias:
 *  - Convite e remocao entram no RASCUNHO e so valem com "Salvar na previa",
 *    como as outras abas. Na versao real, o convite dispara e-mail; deixar
 *    isso acontecer no clique do modal, sem a etapa de salvar, criaria a
 *    unica aba em que "Descartar" nao desfaz nada. Ao salvar, a lista vai
 *    para a pagina (`onSaved`) e a prontidao passa a contar a equipe nova.
 *  - O ultimo dono nao pode ser removido. Sem dono, ninguem altera
 *    configuracoes nem convida gente: a empresa ficaria trancada por fora.
 *  - Papel `platform_admin` nao aparece no convite: e a equipe do Converte
 *    Facil, criada pela administracao, nao pelo lojista.
 *  - Pronta para escala: ordem fixa (donos primeiro, depois por nome), busca
 *    por nome ou e-mail quando a lista passa de 5 pessoas, e "ver mais" de 8
 *    em 8. Uma rede de franquias com 60 vendedoras nao pode virar uma tabela
 *    de 60 linhas na dobra.
 *  - Abaixo de `md` a tabela vira lista de cartoes (RNF16): cinco colunas em
 *    360px nao cabem sem esconder o e-mail.
 */

import { useMemo, useState } from 'react';
import { Badge, Button, Field, Icon, Input, Modal, SectionCard, Select } from '../../../components/ui';
import { formatCount, formatDateTime } from '../../../lib/format';
import type { CompanySummary, TeamMember, UserRole } from '../../../types';
import { useRascunho } from '../useRascunho';
import { ROLE_INFO, ROLES_CONVIDAVEIS, emailValido } from '../vocabulario';
import { BarraSalvar, type ErroDeCampo } from './BarraSalvar';

const PREFIXO_CONVITE = 'SIMULADO-convite-';
const FORM_CONVITE_ID = 'form-convite-equipe';
const ID_CONVITE_NOME = 'cfg-convite-nome';
const ID_CONVITE_EMAIL = 'cfg-convite-email';
const MOSTRAR_BUSCA_A_PARTIR_DE = 6;
const PAGINA = 8;

type PapelConvidavel = Exclude<UserRole, 'platform_admin'>;

interface Convite {
  readonly nome: string;
  readonly email: string;
  readonly papel: PapelConvidavel;
}

const CONVITE_VAZIO: Convite = { nome: '', email: '', papel: 'member' };

function ehPapelConvidavel(valor: string): valor is PapelConvidavel {
  return ROLES_CONVIDAVEIS.some((r) => r === valor);
}

function ehConvite(membro: TeamMember): boolean {
  return membro.id.startsWith(PREFIXO_CONVITE);
}

function ordenar(a: TeamMember, b: TeamMember): number {
  const porPapel = ROLE_INFO[a.role].ordem - ROLE_INFO[b.role].ordem;
  return porPapel !== 0 ? porPapel : a.name.localeCompare(b.name, 'pt-BR');
}

function PapelBadge({ role }: { readonly role: UserRole }): JSX.Element {
  const info = ROLE_INFO[role];
  return <Badge tone={info.tone}>{info.label}</Badge>;
}

export function AbaEquipe({
  base,
  company,
  onSaved,
}: {
  readonly base: readonly TeamMember[];
  readonly company: CompanySummary;
  readonly onSaved: (salvo: readonly TeamMember[]) => void;
}): JSX.Element {
  const rascunho = useRascunho<{ readonly membros: readonly TeamMember[] }>({ membros: base });
  const [convidando, setConvidando] = useState(false);
  const [convite, setConvite] = useState<Convite>(CONVITE_VAZIO);
  const [tentouConvidar, setTentouConvidar] = useState(false);
  const [removendo, setRemovendo] = useState<TeamMember | null>(null);
  const [sequencia, setSequencia] = useState(1);
  const [busca, setBusca] = useState('');
  const [limite, setLimite] = useState(PAGINA);

  const membros = rascunho.draft.membros;
  const donos = membros.filter((m) => m.role === 'owner').length;

  const ordenados = useMemo(() => [...membros].sort(ordenar), [membros]);
  const termo = busca.trim().toLowerCase();
  const filtrados = termo === '' ? ordenados : ordenados.filter((m) => `${m.name} ${m.email}`.toLowerCase().includes(termo));
  const visiveis = filtrados.slice(0, limite);
  const restantes = filtrados.length - visiveis.length;
  const mostrarBusca = membros.length >= MOSTRAR_BUSCA_A_PARTIR_DE;

  const emailNormalizado = convite.email.trim().toLowerCase();
  const erroNome = convite.nome.trim() === '' ? 'Informe o nome.' : null;
  const erroEmail = !emailValido(convite.email)
    ? 'Informe um e-mail válido.'
    : membros.some((m) => m.email.toLowerCase() === emailNormalizado)
      ? 'Essa pessoa já está na equipe.'
      : null;
  const errosDoConvite: readonly ErroDeCampo[] = [
    erroNome === null ? null : { id: ID_CONVITE_NOME, campo: 'Nome', mensagem: erroNome },
    erroEmail === null ? null : { id: ID_CONVITE_EMAIL, campo: 'E-mail', mensagem: erroEmail },
  ].filter((e): e is ErroDeCampo => e !== null);
  const conviteValido = errosDoConvite.length === 0;

  const fecharConvite = (): void => {
    setConvidando(false);
    setConvite(CONVITE_VAZIO);
    setTentouConvidar(false);
  };

  const confirmarConvite = (): void => {
    setTentouConvidar(true);
    if (!conviteValido) {
      return;
    }
    const novo: TeamMember = {
      id: `${PREFIXO_CONVITE}${sequencia}`,
      companyId: company.id,
      name: convite.nome.trim(),
      email: emailNormalizado,
      role: convite.papel,
      lastAccessAt: null,
    };
    rascunho.setDraft((d) => ({ membros: [...d.membros, novo] }));
    setSequencia((s) => s + 1);
    fecharConvite();
  };

  const confirmarRemocao = (): void => {
    const alvo = removendo;
    if (alvo === null) {
      return;
    }
    rascunho.setDraft((d) => ({ membros: d.membros.filter((m) => m.id !== alvo.id) }));
    setRemovendo(null);
  };

  const salvar = (): void => {
    rascunho.salvar();
    onSaved(membros);
  };

  const podeRemover = (membro: TeamMember): boolean => !(membro.role === 'owner' && donos <= 1);
  const motivoNaoRemover = 'A empresa precisa de ao menos um dono.';

  const acessoDe = (membro: TeamMember): string => (membro.lastAccessAt === null ? 'Nunca acessou' : formatDateTime(membro.lastAccessAt));

  const botaoRemover = (membro: TeamMember): JSX.Element => (
    <Button
      size="sm"
      variant="ghost"
      icon="lixeira"
      disabled={!podeRemover(membro)}
      title={podeRemover(membro) ? undefined : motivoNaoRemover}
      onClick={() => setRemovendo(membro)}
    >
      Remover
    </Button>
  );

  return (
    <SectionCard
      id="equipe"
      title="Equipe"
      icon="usuarios"
      description={`Quem acessa o painel de ${company.name}. Dono altera configurações; equipe acompanha funis, leads e contatos.`}
      aside={
        <>
          <Badge tone="neutral" icon="usuarios">
            {formatCount(membros.length)} {membros.length === 1 ? 'pessoa' : 'pessoas'} · {formatCount(donos)} {donos === 1 ? 'dono' : 'donos'}
          </Badge>
          <Button variant="primary" icon="mais" onClick={() => setConvidando(true)}>
            Convidar
          </Button>
        </>
      }
      padded={false}
    >
      {mostrarBusca ? (
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 pb-3">
          <div className="relative w-full max-w-xs">
            <Icon name="busca" size="sm" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
            <Input
              size="sm"
              aria-label="Buscar por nome ou e-mail"
              placeholder="Buscar por nome ou e-mail"
              value={busca}
              onChange={(event) => {
                setBusca(event.target.value);
                setLimite(PAGINA);
              }}
              className="pl-9"
            />
          </div>
          <p className="text-xs text-ink-subtle">
            {formatCount(filtrados.length)} de {formatCount(membros.length)}
          </p>
        </div>
      ) : null}

      {/* Tabela: md para cima. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
              <th scope="col" className="px-5 py-2">
                Nome
              </th>
              <th scope="col" className="px-3 py-2">
                E-mail
              </th>
              <th scope="col" className="px-3 py-2">
                Papel
              </th>
              <th scope="col" className="px-3 py-2">
                Último acesso
              </th>
              <th scope="col" className="px-5 py-2 text-right">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {visiveis.map((membro) => (
              <tr key={membro.id}>
                <td className="px-5 py-3 font-medium text-ink">
                  <span className="flex flex-wrap items-center gap-2">
                    {membro.name}
                    {ehConvite(membro) ? (
                      <Badge tone="info" dot>
                        Convite na prévia
                      </Badge>
                    ) : null}
                  </span>
                </td>
                <td className="px-3 py-3 text-ink-muted">{membro.email}</td>
                <td className="px-3 py-3">
                  <PapelBadge role={membro.role} />
                </td>
                <td className="px-3 py-3 text-ink-muted">{acessoDe(membro)}</td>
                <td className="px-5 py-3 text-right">{botaoRemover(membro)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cartoes: abaixo de md. */}
      <ul className="divide-y divide-line md:hidden">
        {visiveis.map((membro) => (
          <li key={membro.id} className="flex flex-col gap-2 px-5 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-ink">{membro.name}</p>
              <PapelBadge role={membro.role} />
            </div>
            <p className="break-all text-sm text-ink-muted">{membro.email}</p>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-subtle">
              <span>Último acesso: {acessoDe(membro)}</span>
              {ehConvite(membro) ? (
                <Badge tone="info" dot>
                  Convite na prévia
                </Badge>
              ) : null}
            </div>
            <div>{botaoRemover(membro)}</div>
          </li>
        ))}
      </ul>

      {membros.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-ink-muted">Ninguém na equipe. Convide o dono da conta para começar: sem dono, ninguém altera configurações.</p>
      ) : filtrados.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-ink-muted">Ninguém com “{busca.trim()}” no nome ou no e-mail.</p>
      ) : null}

      {restantes > 0 ? (
        <div className="px-5 pt-3">
          <Button variant="ghost" size="sm" icon="chevron" onClick={() => setLimite((l) => l + PAGINA)}>
            Ver mais {formatCount(Math.min(PAGINA, restantes))} de {formatCount(restantes)}
          </Button>
        </div>
      ) : null}

      {membros.length === 1 ? (
        <p className="mx-5 mt-3 flex items-start gap-2 rounded-lg border border-status-warning/50 bg-status-warning/5 px-3 py-2 text-xs text-ink-muted">
          <Icon name="alerta" size="xs" className="mt-0.5 shrink-0 text-status-warning" />
          <span>Só uma pessoa acessa o painel. Convide quem vai responder no WhatsApp e acompanhar os leads: uma pessoa só vira gargalo quando a régua começa a devolver gente.</span>
        </p>
      ) : null}

      <div className="px-5 pb-5 pt-4">
        <BarraSalvar dirty={rascunho.dirty} saved={rascunho.saved} onSave={salvar} onDiscard={rascunho.descartar} />
      </div>

      <Modal
        open={convidando}
        onClose={fecharConvite}
        title="Convidar para a equipe"
        description={`A pessoa recebe um e-mail para criar a senha e entrar no painel de ${company.name}.`}
        footer={
          <>
            <Button variant="ghost" onClick={fecharConvite}>
              Cancelar
            </Button>
            <Button variant="primary" icon="mais" type="submit" form={FORM_CONVITE_ID}>
              Adicionar à prévia
            </Button>
          </>
        }
      >
        <form
          id={FORM_CONVITE_ID}
          className="flex flex-col gap-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            confirmarConvite();
          }}
        >
          <Field label="Nome" required htmlFor={ID_CONVITE_NOME} error={tentouConvidar ? erroNome : null}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={convite.nome}
                onChange={(event) => setConvite((c) => ({ ...c, nome: event.target.value }))}
                autoComplete="off"
              />
            )}
          </Field>
          <Field label="E-mail" required htmlFor={ID_CONVITE_EMAIL} error={tentouConvidar ? erroEmail : null}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                type="email"
                inputMode="email"
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={convite.email}
                onChange={(event) => setConvite((c) => ({ ...c, email: event.target.value }))}
                autoComplete="off"
              />
            )}
          </Field>
          <Field label="Papel" hint={ROLE_INFO[convite.papel].hint}>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={convite.papel}
                onChange={(event) => {
                  const valor = event.target.value;
                  if (ehPapelConvidavel(valor)) {
                    setConvite((c) => ({ ...c, papel: valor }));
                  }
                }}
              >
                {ROLES_CONVIDAVEIS.map((papel) => (
                  <option key={papel} value={papel}>
                    {ROLE_INFO[papel].label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {tentouConvidar && !conviteValido ? (
            <p role="alert" className="text-xs text-status-critical">
              {errosDoConvite.length === 1 ? 'Um campo precisa de atenção.' : `${formatCount(errosDoConvite.length)} campos precisam de atenção.`}
            </p>
          ) : null}
        </form>
      </Modal>

      <Modal
        open={removendo !== null}
        onClose={() => setRemovendo(null)}
        title={removendo === null ? 'Remover da equipe' : `Remover ${removendo.name} da equipe?`}
        description={`A pessoa perde o acesso ao painel de ${company.name}. Na prévia, nada é enviado ao servidor.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemovendo(null)}>
              Cancelar
            </Button>
            <Button variant="danger" icon="lixeira" onClick={confirmarRemocao}>
              Remover
            </Button>
          </>
        }
      >
        {removendo === null ? null : (
          <p className="text-sm text-ink-muted">
            <span className="font-medium text-ink">{removendo.email}</span> · <PapelBadge role={removendo.role} />
          </p>
        )}
      </Modal>
    </SectionCard>
  );
}
